import React from 'react'
import type { DetailCardProps, DetailCardRow } from './DetailCard.types'
import {
  DETAIL_CARD_DEMO_ROWS,
  DETAIL_CARD_DEMO_TITLE,
  DETAIL_CARD_FAMILY_ROWS,
} from './DetailCard.types'
import './DetailCard.css'

const DetailCard: React.FC<DetailCardProps> = ({
  title,
  rows,
  variant = 'plain',
  className = '',
  darkMode = false,
  previewState,
}) => {
  const isPreview = previewState != null

  const surfaceVariant = previewState === 'tinted' ? 'tinted' : variant

  const resolvedTitle =
    title ??
    (previewState === 'family'
      ? 'Family'
      : isPreview
        ? DETAIL_CARD_DEMO_TITLE
        : undefined)

  const resolvedRows = ((): DetailCardRow[] | undefined => {
    if (rows != null) {
      return rows
    }

    if (!isPreview) {
      return undefined
    }

    switch (previewState) {
      case 'family':
        return DETAIL_CARD_FAMILY_ROWS
      case 'with-met':
        return [
          { label: 'Deductible', value: '$50.00', met: true },
          { label: 'Annual maximum', value: '$2,000.00' },
        ]
      case 'with-highlight':
        return [
          { label: 'Annual maximum', value: '$1,500.00 / $2,000.00' },
          { label: 'Remaining', value: '$500.00', highlight: true },
        ]
      case 'with-muted':
        return [
          { label: 'Deductible', value: '$150.00' },
          { label: 'Annual maximum', value: 'None', muted: true },
        ]
      case 'tinted':
        return [
          { label: 'Lifetime maximum', value: '$800.00 / $1,000.00' },
          { label: 'Remaining', value: '$200.00', highlight: true },
        ]
      default:
        return DETAIL_CARD_DEMO_ROWS
    }
  })()

  const classNames = [
    'ds-detail-card',
    `ds-detail-card--${surfaceVariant}`,
    darkMode ? 'ds-detail-card--dark' : '',
    isPreview ? 'ds-detail-card--preview' : '',
    previewState ? `ds-detail-card--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <article className={classNames}>
      {resolvedTitle != null ? (
        <h3 className="ds-detail-card__title">{resolvedTitle}</h3>
      ) : null}
      {resolvedRows != null && resolvedRows.length > 0 ? (
        <div className="ds-detail-card__rows">
          {resolvedRows.map((row, index) => {
            const valueClass = [
              'ds-detail-card__value',
              row.highlight ? 'ds-detail-card__value--highlight' : '',
              row.muted ? 'ds-detail-card__value--muted' : '',
            ]
              .filter(Boolean)
              .join(' ')

            return (
              <div
                key={`${String(row.label)}-${index}`}
                className="ds-detail-card__row"
              >
                <span className="ds-detail-card__label">{row.label}</span>
                <span className={valueClass}>
                  {row.value}
                  {row.met ? (
                    <span className="ds-detail-card__met"> • met</span>
                  ) : null}
                </span>
              </div>
            )
          })}
        </div>
      ) : null}
    </article>
  )
}

export default DetailCard
